import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Printer, FileText, MessageCircle, Pencil, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Mirrors the printed COA layout from the user's reference image.
// All static text is rendered directly; only the dynamic values come from the saved doc.

const HEADER_LEFT_ROWS = [
  ["product_name", "Product Name"],
  ["packing", "Packing"],
  ["generic_name", "Generic Name"],
  ["product_code", "Product Code"],
  ["batch_no", "Batch No."],
  ["actual_batch_size", "Actual Batch Size"],
  ["sample_size", "Sample Size"],
  ["released_qty", "Released Qty"],
  ["remarks", "Remarks"],
];
const HEADER_MID_ROWS = [
  ["mfg_date", "Mfg. Dt."],
  ["exp_date", "Exp. Dt."],
  ["test_packing", "Test Packing"],
  ["license_no", "Mfg. Lic. No."],
  ["test_as_per", "Test As Per"],
];
const HEADER_RIGHT_ROWS = [
  ["ar_no", "A.R. No."],
  ["rel_dt", "Rel. Dt."],
  ["tr_slip_no", "T.R. Slip No."],
  ["tr_slip_dt", "T.R. Slip Dt."],
  ["analysis_date", "Analysis Date"],
  ["specification_no", "Specification No."],
  ["specification_dt", "Specification Dt."],
  ["stp_no", "STP No."],
  ["location", "Location"],
  ["make", "Make"],
];

export default function CoaPreview({ coa, onClose, onEdit }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePrint = () => {
    // Open a clean print window so the rest of the app's UI doesn't bleed in
    const html = ref.current?.outerHTML || "";
    const win = window.open("", "_blank", "width=1100,height=800");
    if (!win) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    win.document.write(`<!doctype html><html><head><title>COA — ${coa.product_name || ""}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 12mm; }
  table { width: 100%; border-collapse: collapse; }
  td, th { border: 1px solid #000; padding: 4px 6px; vertical-align: top; font-size: 11px; }
  th { background: #f1f5f9; font-weight: 700; text-align: center; }
  .coa-title-band { text-align: center; border-bottom: 2px solid #000; padding-bottom: 4px; margin-bottom: 6px; }
  .coa-title { font-weight: 700; font-size: 16px; }
  .coa-subtitle { font-weight: 700; font-size: 13px; }
  .coa-doc-title { font-weight: 800; font-size: 18px; margin: 6px 0 8px; text-transform: uppercase; }
  .coa-header { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; border: 1px solid #000; padding: 8px; margin-bottom: 0; border-bottom: none; }
  .coa-header .row { display: grid; grid-template-columns: 130px 8px 1fr; align-items: start; font-size: 11px; padding: 1px 0; }
  .coa-header .label { font-weight: 700; }
  .coa-header .value { word-break: break-word; }
  .coa-footer-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; border: 1px solid #000; border-top: none; }
  .coa-footer-grid > div { border-right: 1px solid #000; padding: 8px 10px; min-height: 80px; }
  .coa-footer-grid > div:last-child { border-right: none; }
  .signature-label { text-align: center; font-weight: 700; font-size: 12px; padding-bottom: 4px; border-bottom: 1px solid #000; margin-bottom: 36px; }
  .signature-name { text-align: center; font-weight: 700; font-size: 11px; }
  .signature-role { text-align: center; font-size: 10px; }
  .conclusion { border: 1px solid #000; border-top: none; padding: 8px 10px; font-size: 11px; }
  .footer-code { text-align: right; font-size: 9px; padding-top: 2px; color: #555; }
  @media print { @page { size: A4; margin: 8mm; } }
</style></head><body>${html}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 300);
  };

  const buildPdfDoc = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 28;
    let y = margin;

    // Title block
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(`A/C OF ${(coa.company_name || "").toUpperCase()}`.trim(), pageW / 2, y, { align: "center" }); y += 16;
    doc.setFontSize(11);
    doc.text(coa.department_text || "QUALITY CONTROL DEPARTMENT", pageW / 2, y, { align: "center" }); y += 14;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(coa.act_text || "", pageW / 2, y, { align: "center" }); y += 8;
    doc.setLineWidth(1);
    doc.line(margin, y, pageW - margin, y); y += 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(coa.document_title || "FINISHED PRODUCT CERTIFICATE OF ANALYSIS", margin, y); y += 14;

    // Header three-column grid
    const colW = (pageW - margin * 2) / 3;
    const headerStartY = y;
    const drawCol = (rows, x) => {
      let cy = headerStartY + 10;
      doc.setFontSize(8.5);
      rows.forEach(([k, label]) => {
        doc.setFont("helvetica", "bold");
        doc.text(label, x + 4, cy);
        doc.setFont("helvetica", "normal");
        doc.text(":", x + 90, cy);
        const v = String(coa[k] ?? "");
        const lines = doc.splitTextToSize(v, colW - 100);
        doc.text(lines, x + 96, cy);
        cy += Math.max(10, lines.length * 10);
      });
      return cy;
    };
    const e1 = drawCol(HEADER_LEFT_ROWS, margin);
    const e2 = drawCol(HEADER_MID_ROWS, margin + colW);
    const e3 = drawCol(HEADER_RIGHT_ROWS, margin + colW * 2);
    const headerEndY = Math.max(e1, e2, e3) + 6;
    // Border around header
    doc.setLineWidth(0.7);
    doc.rect(margin, headerStartY, pageW - margin * 2, headerEndY - headerStartY);
    doc.line(margin + colW, headerStartY, margin + colW, headerEndY);
    doc.line(margin + colW * 2, headerStartY, margin + colW * 2, headerEndY);
    y = headerEndY;

    // Test table
    const head = [["Sr.", "Test", "Result", "Specification"]];
    const body = (coa.tests || []).map((t, i) => [String(i + 1), t.test || "", t.result || "", t.specification || ""]);
    autoTable(doc, {
      head,
      body,
      startY: y,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.5, overflow: "linebreak" },
      headStyles: { fillColor: [241, 245, 249], textColor: [0, 0, 0], fontStyle: "bold", lineColor: [0, 0, 0] },
      columnStyles: { 0: { halign: "center", cellWidth: 24 }, 1: { cellWidth: 130 }, 2: { cellWidth: 170 } },
    });
    y = doc.lastAutoTable.finalY;

    // Conclusion
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Conclusion :", margin + 4, y + 14);
    doc.setFont("helvetica", "normal");
    const concText = doc.splitTextToSize(coa.conclusion || "", pageW - margin * 2 - 80);
    doc.text(concText, margin + 80, y + 14);
    y += 14 + Math.max(10, concText.length * 10) + 6;
    doc.rect(margin, doc.lastAutoTable.finalY, pageW - margin * 2, y - doc.lastAutoTable.finalY);

    // Signatures
    const sigH = 70;
    const cw = (pageW - margin * 2) / 3;
    [["Prepared By", coa.prepared_by, coa.prepared_role], ["Checked By", coa.checked_by, coa.checked_role], ["Approved By", coa.approved_by, coa.approved_role]].forEach(([lbl, name, role], i) => {
      const sx = margin + cw * i;
      doc.rect(sx, y, cw, sigH);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(lbl, sx + cw / 2, y + 12, { align: "center" });
      doc.setLineWidth(0.4);
      doc.line(sx + 8, y + 18, sx + cw - 8, y + 18);
      doc.setFontSize(9);
      doc.text(String(name || "").toUpperCase(), sx + cw / 2, y + sigH - 16, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(String(role || "").toUpperCase(), sx + cw / 2, y + sigH - 6, { align: "center" });
    });
    y += sigH;

    // Footer code
    if (coa.footer_code) {
      doc.setFontSize(8);
      doc.setTextColor(90);
      doc.text(coa.footer_code, pageW - margin, y + 12, { align: "right" });
    }

    const filename = `COA_${(coa.product_name || "doc").replace(/\s+/g, "_")}_${(coa.batch_no || "").replace(/\s+/g, "_") || "draft"}.pdf`;
    return { doc, filename };
  };

  const handleDownloadPDF = () => {
    setBusy(true);
    try {
      const { doc, filename } = buildPdfDoc();
      doc.save(filename);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e); toast.error("PDF failed: " + e.message);
    } finally { setBusy(false); }
  };

  const handleShareWhatsApp = async () => {
    setBusy(true);
    try {
      const { doc, filename } = buildPdfDoc();
      const blob = doc.output("blob");
      const file = new File([blob], filename, { type: "application/pdf" });
      const text = `Certificate of Analysis — ${coa.product_name || ""} (Batch: ${coa.batch_no || "-"})`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename, text });
          toast.success("Shared");
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
        }
      }
      // Fallback
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      toast.success("PDF downloaded — attach it in WhatsApp");
    } catch (e) {
      toast.error("Share failed: " + e.message);
    } finally { setBusy(false); }
  };

  const cell = (key, label) => (
    <div className="grid grid-cols-[130px_8px_1fr] items-start py-0.5">
      <span className="text-[11px] font-bold uppercase tracking-tight">{label}</span>
      <span>:</span>
      <span className="text-[11px] break-words">{String(coa[key] ?? "")}</span>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="coa-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 sticky top-0 z-10 bg-background py-2 print:hidden">
        <Button variant="outline" size="sm" onClick={onClose} data-testid="coa-preview-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onEdit} className="gap-1.5" data-testid="coa-preview-edit">
            <Pencil className="w-4 h-4" /> Edit
          </Button>
          <Button size="sm" onClick={handlePrint} className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-white" data-testid="coa-print-btn">
            <Printer className="w-4 h-4" /> Print
          </Button>
          <Button size="sm" onClick={handleDownloadPDF} disabled={busy} className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white" data-testid="coa-pdf-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
          </Button>
          <Button size="sm" onClick={handleShareWhatsApp} disabled={busy} className="gap-1.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white" data-testid="coa-whatsapp-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} WhatsApp
          </Button>
        </div>
      </div>

      {/* Printable area */}
      <div className="border rounded-lg overflow-auto bg-white text-black">
        <div ref={ref} className="p-4 sm:p-6 max-w-[210mm] mx-auto" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
          <div className="coa-title-band text-center border-b-2 border-black pb-1 mb-2">
            <div className="coa-title font-bold text-lg">A/C OF {(coa.company_name || "").toUpperCase()}</div>
            <div className="coa-subtitle font-bold text-sm">{coa.department_text || "QUALITY CONTROL DEPARTMENT"}</div>
            <div className="text-[11px]">{coa.act_text || ""}</div>
          </div>
          <div className="coa-doc-title font-extrabold text-base sm:text-lg my-2 uppercase">{coa.document_title || "FINISHED PRODUCT CERTIFICATE OF ANALYSIS"}</div>

          <div className="coa-header grid grid-cols-1 sm:grid-cols-3 border border-black p-2 gap-x-3" style={{ borderBottom: "none" }}>
            <div>{HEADER_LEFT_ROWS.map(([k, l]) => <div key={k}>{cell(k, l)}</div>)}</div>
            <div>{HEADER_MID_ROWS.map(([k, l]) => <div key={k}>{cell(k, l)}</div>)}</div>
            <div>{HEADER_RIGHT_ROWS.map(([k, l]) => <div key={k}>{cell(k, l)}</div>)}</div>
          </div>

          <table className="w-full border border-black border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-black text-center w-12 py-1 text-xs">Sr.</th>
                <th className="border border-black py-1 text-xs">Test</th>
                <th className="border border-black py-1 text-xs">Result</th>
                <th className="border border-black py-1 text-xs">Specification</th>
              </tr>
            </thead>
            <tbody>
              {(coa.tests || []).map((t, i) => (
                <tr key={i} className="align-top">
                  <td className="border border-black text-center font-mono text-[11px] py-1">{i + 1}</td>
                  <td className="border border-black px-2 py-1 text-[11px] whitespace-pre-wrap">{t.test}</td>
                  <td className="border border-black px-2 py-1 text-[11px] whitespace-pre-wrap">{t.result}</td>
                  <td className="border border-black px-2 py-1 text-[11px] whitespace-pre-wrap">{t.specification}</td>
                </tr>
              ))}
              {(coa.tests || []).length === 0 && (
                <tr><td colSpan={4} className="text-center text-xs text-muted-foreground py-4">No test rows</td></tr>
              )}
            </tbody>
          </table>

          <div className="conclusion border border-black border-t-0 px-2 py-1.5">
            <div className="text-[11px]"><span className="font-bold">Conclusion :</span> {coa.conclusion || ""}</div>
          </div>

          <div className="coa-footer-grid grid grid-cols-3 border border-black border-t-0">
            <SignBox label="Prepared By" name={coa.prepared_by} role={coa.prepared_role} />
            <SignBox label="Checked By" name={coa.checked_by} role={coa.checked_role} mid />
            <SignBox label="Approved By" name={coa.approved_by} role={coa.approved_role} />
          </div>

          {coa.footer_code && (
            <div className="text-right text-[9px] text-slate-500 mt-1">{coa.footer_code}</div>
          )}
        </div>
      </div>
    </div>
  );
}

const SignBox = ({ label, name, role, mid }) => (
  <div className={`px-3 py-2 ${mid ? "border-l border-r border-black" : ""}`} style={{ minHeight: 90 }}>
    <div className="text-center font-bold text-[11px] border-b border-black pb-1 mb-8">{label}</div>
    <div className="text-center font-bold text-[11px] uppercase">{name || ""}</div>
    <div className="text-center text-[10px] uppercase">{role || ""}</div>
  </div>
);
